/**
 * Soft-launch PIN vs session vs hop — pure so it can be unit-tested
 * without mounting React.
 *
 * PIN is per-origin on purpose. A signed-in person must never see the
 * keypad. World hops must start SSO on this site (`/auth/hop`) so Passport
 * can silently issue a code if they already have a Passport login.
 */

import { ROUTES } from '@/config/routes';

export const PIN_EXEMPT_PATHS: readonly string[] = [
  ROUTES.authCallback,
  ROUTES.authHop,
];

export type PinSurface = 'children' | 'hold' | 'keypad';
export type HopAction = 'hold' | 'home' | 'passport';

export function isPinExemptPath(pathname: string): boolean {
  return PIN_EXEMPT_PATHS.includes(pathname);
}

/**
 * What RequirePin should render.
 *
 * `authenticated === undefined` means we have not finished reading the
 * session — hold on ink, never flash the keypad at someone who turns out
 * to be signed in.
 */
export function resolvePinSurface({
  pathname,
  authenticated,
  pinEnabled,
  pinUnlocked,
}: {
  pathname: string;
  authenticated: boolean | undefined;
  pinEnabled: boolean;
  pinUnlocked: boolean;
}): PinSurface {
  if (isPinExemptPath(pathname)) return 'children';
  if (!pinEnabled) return 'children';
  if (authenticated === undefined) return 'hold';
  if (authenticated) return 'children';
  return pinUnlocked ? 'children' : 'keypad';
}

/**
 * Same-origin path only. Rejects protocol-relative (`//evil`) and anything
 * that is not a site-relative path. Default home is `/`.
 */
export function sameOriginReturnPath(
  raw: string | null | undefined,
  fallback = ROUTES.landing,
): string {
  if (!raw) return fallback;
  if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
  return raw;
}

/**
 * What `/auth/hop` should do once we know (or do not yet know) the session.
 * Strangers with Passport go to SSO on this origin. Everyone else goes home;
 * the PIN wall then handles people who are not signed in.
 */
export function resolveHopAction({
  authenticated,
  passportEnabled,
}: {
  authenticated: boolean | undefined;
  passportEnabled: boolean;
}): HopAction {
  if (authenticated === undefined) return 'hold';
  if (authenticated) return 'home';
  return passportEnabled ? 'passport' : 'home';
}
